/**
 * @file: useWeixinLogin.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { config } from '@euler/config';
import {
  AuthenticatorResult,
  AuthProviderType,
  WeixinAuthProvider,
} from '@euler/model/auth';
import { useServiceFactory } from '@euler/services/factory';
import { makeDebug } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { nanoid } from 'nanoid/non-secure';
import qs from 'qs';
import { Platform } from 'react-native';
import * as WeChat from 'react-native-wechat-lib';

const debug = makeDebug('auth:login:weixin');

const errorMsgByCode = (errCode?: number | null) => {
  // https://developers.weixin.qq.com/doc/oplatform/Mobile_App/WeChat_Login/Development_Guide.html
  return errCode === -4
    ? '已拒绝授权'
    : errCode === -2
    ? '已取消授权'
    : '微信未授权';
};

export type WeixinLoginErrorResult =
  | {
      type: 'weixin_not_installed';
    }
  | {
      type: 'weixin_sdk_error';
      errorCode?: number;
      message?: string;
    }
  | {
      type: 'internal_server_error';
      error: Error;
    };

export const useWeixinLogin = (props: {
  uid?: string;
  onSuccess: (
    nonce: string,
    result: AuthenticatorResult<WeixinAuthProvider>,
  ) => void;
  onError: (result: WeixinLoginErrorResult) => void;
}) => {
  const { authService } = useServiceFactory();
  const { uid, onSuccess, onError } = props;

  return usePersistFn(async () => {
    if (Platform.OS === 'web') return;
    try {
      const installed = await WeChat.isWXAppInstalled();
      if (!installed) {
        onError({ type: 'weixin_not_installed' });
        return;
      }

      const nonce = nanoid(64);
      debug('weixin auth nonce: %s', nonce);
      const state = qs.stringify({ nonce });
      const resp = await WeChat.sendAuthRequest('snsapi_userinfo', state);
      debug('weixin login result: ', resp);

      if (!resp.code) {
        onError({
          type: 'weixin_sdk_error',
          errorCode: resp.errCode,
          message: errorMsgByCode(resp.errCode),
        });
        return;
      }

      try {
        const result = await authService.login({
          type: AuthProviderType.Weixin,
          appId: config.weixinOpen.appId,
          nonce,
          credential: { type: 'code', code: resp.code, uid },
        });

        onSuccess(nonce, result);
      } catch (e) {
        debug('weixin login failed: ', e);
        onError({
          type: 'internal_server_error',
          error: e as Error,
        });
      }
    } catch (e) {
      const error = e as { name: string; code: number };
      debug('weixin login failed: ', e, typeof e, JSON.stringify(e));
      onError({
        type: 'weixin_sdk_error',
        errorCode: error.code,
        message: errorMsgByCode(error.code),
      });
    }
  });
};
